import { type ChildProcess, spawn } from "node:child_process";
import type { EventEmitter } from "node:events";
import { createInterface, type Interface } from "node:readline";
import type { Logger, TokenUsage } from "@skaile/workspaces/types";
import { classifyGenericErrorMessage } from "../error-classifier.js";
import {
  type AgentConfig,
  AgentDriver,
  type AgentError,
  type AgentEvent,
  type AgentMessage,
} from "../types.js";

/**
 * Shared base for one-shot **subprocess CLI** agent backends (Gemini, Qwen,
 * OpenCode, Goose, Continue).
 *
 * These backends are neither persistent JSON-RPC subprocesses (`omp`) nor
 * in-process SDKs (`claude-sdk`, `codex`). In headless mode each is invoked
 * **once per turn** — `prompt()` spawns the CLI fresh, a `readline` loop maps
 * newline-delimited JSON on stdout to the unified {@link AgentEvent} stream, and
 * **process exit marks the end of the turn**. Multi-turn continuity comes from
 * capturing a backend session id (emitted as `session_info`) and replaying it
 * via a resume flag on the next spawn.
 *
 * Subclasses implement {@link buildArgs}, {@link buildEnv}, and either
 * {@link handleJson} (streaming JSONL, the default) or {@link handleBuffered}
 * (`mode = "buffered"`, for CLIs that print a single JSON blob at the end).
 *
 * **Terminal-sequence invariant.** Subclasses MUST NOT emit `agent_end`. The
 * base emits exactly one `result` (synthesizing a success result when the
 * stream produced none) followed by a single `agent_end` on clean exit, or an
 * `error` + `agent_end` on crash. This keeps the turn-completion contract
 * (`prompt()` resolves after `agent_end`) identical across every CLI driver.
 *
 * @docLink packages/bridge/drivers#subprocess-cli
 */
export abstract class SubprocessCliDriver extends AgentDriver {
  /** Per-driver logger, constructed once by the subclass. */
  protected abstract readonly log: Logger;
  /** Default binary name spawned in production (e.g. `"gemini"`). */
  protected abstract readonly defaultBin: string;
  /** Human product name used in error messages / hints (e.g. `"Gemini CLI"`). */
  protected abstract readonly productName: string;
  /**
   * Test-seam env prefix. `<PREFIX>_BRIDGE_BIN` overrides the spawn binary and
   * `<PREFIX>_BRIDGE_PREARGS` (space-delimited) supplies leading argv — used by
   * the fake-CLI harness to redirect spawn without monkey-patching
   * `child_process`. Both are ignored in production.
   */
  protected abstract readonly envPrefix: string;
  /** `"jsonl"` streams events line-by-line; `"buffered"` parses all stdout once on exit. */
  protected readonly mode: "jsonl" | "buffered" = "jsonl";

  protected config: AgentConfig;

  private proc: ChildProcess | null = null;
  private rl: Interface | null = null;
  private stderrBuffer: string[] = [];
  private stdoutBuffer = "";
  private started = false;

  // ── Per-turn state ──────────────────────────────────────────────────────
  private prevText = "";
  private activeAssistant = false;
  private pendingToolResults: AgentMessage[] = [];
  private sawResult = false;
  private aborting = false;
  private turnResolve: (() => void) | null = null;
  private turnReject: ((err: Error) => void) | null = null;

  protected _sessionId?: string;
  protected _lastTokens: TokenUsage | null = null;

  constructor(config: AgentConfig) {
    super();
    this.config = config;
  }

  override get runtimeSessionId(): string | undefined {
    return this._sessionId;
  }

  override getModel(): string | undefined {
    return this.config.model;
  }

  override getTokenUsage(): TokenUsage | null {
    return this._lastTokens;
  }

  override reconfigure(patch: Partial<Pick<AgentConfig, "model" | "thinking" | "effort">>): void {
    if (patch.model !== undefined) this.config.model = patch.model;
    if (patch.thinking !== undefined) this.config.thinking = patch.thinking;
    if (patch.effort !== undefined) this.config.effort = patch.effort;
  }

  /**
   * No-op for one-shot CLIs — there is no long-lived process to spawn until the
   * first prompt. Kept so the runner's `start()` → `prompt()` ordering works
   * uniformly; binary-presence failures surface as a `config` error on the
   * first spawn (see {@link classifyProcessError}).
   */
  async start(): Promise<void> {
    this.started = true;
  }

  get isRunning(): boolean {
    return this.proc !== null && !this.proc.killed;
  }

  /**
   * Sends a user message to the CLI and resolves after the turn completes
   * (`agent_end`). Spawns a fresh child per call; rejects if the child exits
   * non-zero before emitting a result, so the caller never hangs.
   */
  async prompt(message: string): Promise<void> {
    if (this.proc) throw new Error(`${this.productName} turn already in progress`);
    if (!this.started) await this.start();

    this.prevText = "";
    this.activeAssistant = false;
    this.pendingToolResults = [];
    this.sawResult = false;
    this.aborting = false;
    this.stderrBuffer = [];
    this.stdoutBuffer = "";
    this._lastTokens = null;

    const composed = this.composePrompt(message);
    const overrideBin = process.env[`${this.envPrefix}_BRIDGE_BIN`];
    const preArgs = process.env[`${this.envPrefix}_BRIDGE_PREARGS`]
      ? (process.env[`${this.envPrefix}_BRIDGE_PREARGS`] as string).split(/\s+/).filter(Boolean)
      : [];
    const bin = overrideBin || this.defaultBin;
    const args = [...preArgs, ...this.buildArgs(composed, this._sessionId)];
    const env = this.buildEnv();

    return new Promise<void>((resolve, reject) => {
      this.turnResolve = resolve;
      this.turnReject = reject;
      this.log.info("spawning CLI", {
        bin,
        cwd: this.config.cwd,
        resume: Boolean(this._sessionId),
      });

      const proc = spawn(bin, args, {
        cwd: this.config.cwd,
        env,
        stdio: ["ignore", "pipe", "pipe"],
      }) as ChildProcess;
      this.proc = proc;
      const procEmitter = proc as unknown as EventEmitter;

      procEmitter.on("error", (err: Error) => {
        const detail = this.classifyProcessError(err as NodeJS.ErrnoException);
        this.log.error("spawn error", err, { detail });
        this.pushError(detail);
        this.emit("agent-event", { type: "agent_end" } satisfies AgentEvent);
        this.settleReject(Object.assign(new Error(detail.message), { detail }));
      });

      if (proc.stdout) {
        if (this.mode === "jsonl") {
          const rl = createInterface({ input: proc.stdout });
          this.rl = rl;
          (rl as unknown as EventEmitter).on("line", (line: string) => this.onStdoutLine(line));
        } else {
          proc.stdout.setEncoding("utf8");
          proc.stdout.on("data", (chunk: string) => {
            this.stdoutBuffer += chunk;
          });
        }
      }

      if (proc.stderr) {
        const stderrRl = createInterface({ input: proc.stderr });
        (stderrRl as unknown as EventEmitter).on("line", (line: string) => {
          this.log.debug("stderr", { line });
          this.stderrBuffer.push(line);
          if (this.stderrBuffer.length > 20) this.stderrBuffer.shift();
        });
      }

      procEmitter.on("exit", (code: number | null, signal: NodeJS.Signals | null) => {
        this.log.info("CLI exited", { code, signal });
        this.onExit(code, signal);
      });
    });
  }

  /** Out-of-band abort: terminates the current child. Driver stays usable for the next prompt. */
  async abort(): Promise<void> {
    this.aborting = true;
    this.proc?.kill("SIGTERM");
  }

  kill(): void {
    this.aborting = true;
    this.proc?.kill("SIGTERM");
    this.cleanup();
  }

  async resetSession(): Promise<void> {
    this.kill();
    this._sessionId = undefined;
    this._lastTokens = null;
  }

  // ── Subclass hooks ────────────────────────────────────────────────────────

  /** Build the CLI argv (binary excluded) for one turn. `resumeId` is the captured session id, if any. */
  protected abstract buildArgs(message: string, resumeId: string | undefined): string[];

  /** Build the child-process env (explicit allowlist — see {@link baseEnv}). */
  protected abstract buildEnv(): Record<string, string>;

  /** Map one parsed stdout JSON object to events via the `push*` helpers. Streaming (`jsonl`) mode only. */
  protected handleJson(_json: unknown): void {}

  /** Parse the full stdout blob once on clean exit. `buffered` mode only. */
  protected handleBuffered(_raw: string): void {}

  /** Install-failure hint surfaced when the binary is missing. */
  protected abstract installHint(): string;

  /**
   * Classify a non-zero exit into an {@link AgentError}, or `null` to treat the
   * exit as success (e.g. a turn-limit code where a result was already
   * emitted). Default: code `0` / clean signal → success; otherwise `process`.
   */
  protected classifyExit(code: number | null, signal: NodeJS.Signals | null): AgentError | null {
    if (code === 0 || (code === null && signal === null)) return null;
    const tail = this.stderrBuffer.slice(-10).join("\n");
    return {
      message: `${this.productName} exited with code ${code ?? "null"}${tail ? `: ${tail}` : ""}`,
      category: "process",
      retryable: true,
      hint: tail || `The ${this.productName} process exited unexpectedly. Try the prompt again.`,
    };
  }

  // ── Protected emit helpers (used by handleJson / handleBuffered) ────────────

  /** Prepend `systemPrompt` to the user message — uniform across CLIs lacking a reliable system flag. */
  protected composePrompt(message: string): string {
    return this.config.systemPrompt ? `${this.config.systemPrompt}\n\n---\n\n${message}` : message;
  }

  protected pushAssistantDelta(fullText: string): void {
    if (!this.activeAssistant) {
      this.activeAssistant = true;
      this.prevText = "";
      this.emit("agent-event", {
        type: "message_start",
        message: asstMessage(""),
      } satisfies AgentEvent);
    }
    const delta = fullText.startsWith(this.prevText)
      ? fullText.slice(this.prevText.length)
      : fullText;
    this.prevText = fullText;
    if (delta) {
      this.emit("agent-event", {
        type: "message_update",
        message: asstMessage(fullText),
        _textDelta: delta,
      } satisfies AgentEvent);
    }
  }

  protected pushAssistantEnd(fullText?: string): void {
    if (!this.activeAssistant) return;
    const text = fullText ?? this.prevText;
    this.emit("agent-event", {
      type: "message_end",
      message: asstMessage(text),
    } satisfies AgentEvent);
    this.activeAssistant = false;
    this.prevText = "";
  }

  protected pushToolCall(name: string, input: unknown): void {
    this.emit("agent-event", {
      type: "tool_call",
      name,
      tool: { name },
      input,
    } satisfies AgentEvent);
  }

  protected pushToolResult(name: string, data: unknown, isError = false): void {
    this.pendingToolResults.push({ role: "tool", toolName: name, content: "", data, isError });
    this.emit("agent-event", {
      type: "tool_execution_end",
      toolName: name,
    } satisfies AgentEvent);
  }

  private flushToolResults(): void {
    if (this.pendingToolResults.length === 0) return;
    this.emit("agent-event", {
      type: "turn_end",
      toolResults: [...this.pendingToolResults],
    } satisfies AgentEvent);
    this.pendingToolResults = [];
  }

  protected pushResult(opts: {
    tokens?: TokenUsage | null;
    costUsd?: number;
    summary?: string;
    subtype?: string;
  }): void {
    this.pushAssistantEnd();
    this.flushToolResults();
    if (opts.tokens) this._lastTokens = opts.tokens;
    this.sawResult = true;
    this.emit("agent-event", {
      type: "result",
      subtype: opts.subtype ?? "success",
      summary: opts.summary,
      costUsd: opts.costUsd ?? 0,
      ...(opts.tokens ? { tokens: opts.tokens } : {}),
    } satisfies AgentEvent);
  }

  protected pushError(detail: AgentError, fatal = false): void {
    this.emit("agent-event", {
      type: "error",
      error: detail.message,
      detail,
      fatal,
    } satisfies AgentEvent);
  }

  protected captureSession(id?: string, file?: string): void {
    if (!id || id === this._sessionId) return;
    this._sessionId = id;
    this.emit("agent-event", {
      type: "session_info",
      driverSessionId: id,
      ...(file ? { sessionFile: file } : {}),
    } satisfies AgentEvent);
  }

  /** Shared keyword classification + per-driver hint copy (mirrors omp/codex). */
  protected classifyMessage(message: string): AgentError {
    const generic = classifyGenericErrorMessage(message);
    if (!generic) {
      return {
        message,
        category: "unknown",
        retryable: true,
        hint: `An unexpected ${this.productName} error occurred. Try the prompt again.`,
      };
    }
    const hint =
      generic.category === "auth"
        ? `Check the API key / login configured for ${this.productName}.`
        : generic.category === "rate_limit"
          ? `${this.productName} hit a provider rate limit. Wait a moment and try again.`
          : generic.category === "model"
            ? "The selected model is unavailable or the context is too long. Change the model or start a new session."
            : `A network error occurred while talking to ${this.productName}.`;
    return { message, category: generic.category, retryable: generic.retryable, hint };
  }

  // ── Env allowlist ──────────────────────────────────────────────────────────

  /**
   * Explicit env whitelist forwarded to the child. Omits anything provisioned
   * by `provision_secrets` so user credentials stay out of the subprocess
   * environment. `config.env`, when supplied, fully overrides the whitelist.
   * Always passes through the driver's test-seam vars and `FAKE_CLI_*`.
   */
  protected baseEnv(extraKeys: string[] = []): Record<string, string> {
    if (this.config.env) return { ...this.config.env };
    const out: Record<string, string> = {};
    const explicit = ["PATH", "HOME", "USER", "HOSTNAME", "SKAILE_SESSION_ID", ...extraKeys];
    for (const key of explicit) {
      const v = process.env[key];
      if (v !== undefined) out[key] = v;
    }
    for (const [key, value] of Object.entries(process.env)) {
      if (typeof value !== "string") continue;
      if (key.startsWith(`${this.envPrefix}_BRIDGE_`) || key.startsWith("FAKE_CLI_")) {
        out[key] = value;
      }
    }
    if (this.config.sshKeyPath) {
      out.GIT_SSH_COMMAND = `ssh -i "${this.config.sshKeyPath}" -o StrictHostKeyChecking=accept-new`;
    }
    return out;
  }

  // ── Internals ────────────────────────────────────────────────────────────

  private onStdoutLine(line: string): void {
    if (!line.trim()) return;
    let json: unknown;
    try {
      json = JSON.parse(line);
    } catch {
      this.log.debug("non-JSON stdout line", { line });
      return;
    }
    try {
      this.handleJson(json);
    } catch (err) {
      this.log.warn("handleJson threw", { error: String(err) });
    }
  }

  private onExit(code: number | null, signal: NodeJS.Signals | null): void {
    this.cleanup();

    if (this.aborting) {
      this.pushAssistantEnd();
      this.flushToolResults();
      this.emit("agent-event", { type: "agent_end" } satisfies AgentEvent);
      this.settleResolve();
      return;
    }

    if (this.mode === "buffered" && (code === 0 || code === null)) {
      try {
        this.handleBuffered(this.stdoutBuffer);
      } catch (err) {
        this.log.warn("handleBuffered threw", { error: String(err) });
      }
    }

    const exitErr = this.classifyExit(code, signal);
    if (exitErr && !this.sawResult) {
      this.pushAssistantEnd();
      this.flushToolResults();
      this.pushError(exitErr);
      this.emit("agent-event", { type: "agent_end" } satisfies AgentEvent);
      this.settleReject(Object.assign(new Error(exitErr.message), { detail: exitErr }));
      return;
    }

    // Clean exit (or non-zero exit after a result already streamed): close any
    // open assistant message, synthesize a success result if none arrived, end.
    if (!this.sawResult) this.pushResult({ summary: "Task completed" });
    else {
      this.pushAssistantEnd();
      this.flushToolResults();
    }
    this.emit("agent-event", { type: "agent_end" } satisfies AgentEvent);
    this.settleResolve();
  }

  private settleResolve(): void {
    const resolve = this.turnResolve;
    this.turnResolve = null;
    this.turnReject = null;
    resolve?.();
  }

  private settleReject(err: Error): void {
    const reject = this.turnReject;
    this.turnResolve = null;
    this.turnReject = null;
    reject?.(err);
  }

  private cleanup(): void {
    this.rl?.close();
    this.rl = null;
    this.proc = null;
  }

  private classifyProcessError(err: NodeJS.ErrnoException): AgentError {
    if (err.code === "ENOENT") {
      return {
        message: `${this.defaultBin} command not found`,
        category: "config",
        retryable: false,
        hint: this.installHint(),
      };
    }
    if (err.code === "EACCES") {
      return {
        message: `Permission denied running ${this.defaultBin}`,
        category: "config",
        retryable: false,
        hint: `Check file permissions for the ${this.defaultBin} binary.`,
      };
    }
    return {
      message: err.message,
      category: "process",
      retryable: true,
      hint: `An unexpected process error occurred running ${this.productName}. Try again.`,
    };
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Tolerant JSON extractors — shared because the upstream stream-json schemas
// are not fully pinned; read the common field-name variants defensively.
// ───────────────────────────────────────────────────────────────────────────

function asstMessage(text: string): AgentMessage {
  return { role: "assistant", content: [{ type: "text", text }] };
}

/** Pull assistant text out of a CLI event, trying the common field names. */
export function extractText(json: any): string {
  if (typeof json?.text === "string") return json.text;
  if (typeof json?.delta === "string") return json.delta;
  if (typeof json?.content === "string") return json.content;
  if (typeof json?.response === "string") return json.response;
  const msg = json?.message;
  if (typeof msg?.content === "string") return msg.content;
  if (Array.isArray(msg?.content)) {
    return msg.content
      .filter(
        (b: any) => (b?.type === "text" || b?.type === undefined) && typeof b?.text === "string",
      )
      .map((b: any) => b.text)
      .join("");
  }
  if (Array.isArray(json?.parts)) {
    return json.parts
      .filter((p: any) => typeof p?.text === "string")
      .map((p: any) => p.text)
      .join("");
  }
  return "";
}

/** Map a CLI usage/stats object onto {@link TokenUsage}, trying common field names. */
export function extractTokens(json: any): TokenUsage | null {
  const u = json?.usage ?? json?.stats?.usage ?? json?.stats ?? json?.tokens ?? json;
  if (!u || typeof u !== "object") return null;
  const input = u.input_tokens ?? u.inputTokens ?? u.prompt_tokens ?? u.promptTokenCount ?? u.input;
  const output =
    u.output_tokens ?? u.outputTokens ?? u.completion_tokens ?? u.candidatesTokenCount ?? u.output;
  const cacheRead = u.cached_input_tokens ?? u.cacheReadTokens ?? u.cachedContentTokenCount;
  if (input === undefined && output === undefined) return null;
  const tokens: TokenUsage = {};
  if (typeof input === "number") tokens.inputTokens = input;
  if (typeof output === "number") tokens.outputTokens = output;
  if (typeof cacheRead === "number") tokens.cacheReadTokens = cacheRead;
  return tokens;
}

/** Pull a per-turn USD cost from a CLI result object, or `undefined`. */
export function extractCost(json: any): number | undefined {
  const c =
    json?.cost ?? json?.costUsd ?? json?.total_cost_usd ?? json?.stats?.cost ?? json?.usage?.cost;
  return typeof c === "number" ? c : undefined;
}

/** Map known `apiKeys` provider names onto the env var names provider CLIs expect. */
export function mapApiKeysToEnv(
  apiKeys: Record<string, string> | undefined,
): Record<string, string> {
  if (!apiKeys) return {};
  const map: Record<string, string> = {
    anthropic: "ANTHROPIC_API_KEY",
    openai: "OPENAI_API_KEY",
    google: "GEMINI_API_KEY",
    gemini: "GEMINI_API_KEY",
    openrouter: "OPENROUTER_API_KEY",
    groq: "GROQ_API_KEY",
    mistral: "MISTRAL_API_KEY",
    deepseek: "DEEPSEEK_API_KEY",
    xai: "XAI_API_KEY",
    together: "TOGETHER_API_KEY",
    fireworks: "FIREWORKS_API_KEY",
  };
  const out: Record<string, string> = {};
  for (const [provider, key] of Object.entries(apiKeys)) {
    const envName = map[provider];
    if (envName && key) out[envName] = key;
  }
  return out;
}
